import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { KIND_DRAG } from './TreeBoard.tsx';
import { BandMark } from './Tile.tsx';
import { stepName } from './model.ts';
import { TOOL_KINDS, type ToolKind } from './kinds.ts';
import { findMatches, type FindStep } from './find.ts';

/**
 * The tools at the top of a tree (design study 09, adding A; study 12, own step A): the kinds a step can be, each
 * dragged into a phase, or chosen and then added with the phase's own button (for a keyboard, or a touch screen); and
 * Find a step, which rings the step on the board and brings it into view. Setting a job up, Find finds every step of the
 * tree (Jason, 2026-10-08, L-18 C), each with where it stands and what can be done with it from the list: shown, put
 * back where it was filed away, or added (`FindStep`).
 */

const WorkMark = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
    <rect x="2" y="3" width="12" height="10" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

export type { FindStep };

export function TreeTools({
  label,
  kinds = TOOL_KINDS,
  arming,
  onArm,
  find,
  end,
}: {
  /** What adding does, before the kinds: "Add a step", "Add to this job". */
  label: string;
  kinds?: ToolKind[];
  arming: ToolKind | null;
  onArm: (k: ToolKind | null) => void;
  /** What Find lists (`heading` names it: "Steps of the tree"), and what choosing one does: rings it on the board. */
  find: { steps: FindStep[]; onFind: (key: string) => void; heading?: string };
  /** What sits at the bar's right end (saved, Undo). */
  end?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-y border-rule py-2.5">
      {/* With nothing to add (a job's Process page, run, never set up there), only Find a step: no word about dragging. */}
      {kinds.length ? <span className="label mr-1">{label}</span> : null}
      {kinds.map((k) => (
        <button
          key={k.kind}
          type="button"
          draggable
          aria-pressed={arming?.kind === k.kind}
          onDragStart={(e) => {
            e.dataTransfer.setData(KIND_DRAG, k.kind);
            e.dataTransfer.effectAllowed = 'copy';
          }}
          onClick={() => onArm(arming?.kind === k.kind ? null : k)}
          className={`inline-flex h-11 cursor-grab items-center gap-2 rounded-[2px] border px-3.5 text-[0.9375rem] active:cursor-grabbing ${arming?.kind === k.kind ? 'border-ink bg-ink text-paper' : 'border-ink bg-sheet text-ink hover:bg-paper'}`}
        >
          {k.mark === 'work' ? <WorkMark /> : <BandMark mark={k.mark} size={14} />}
          {k.word}
        </button>
      ))}
      {kinds.length ? <span className="meta">{arming ? `Now press Add ${arming.as} here in a phase, or drag it in.` : 'Drag one into a phase.'}</span> : null}
      <span className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-2">
        <FindAStep {...find} />
        {end}
      </span>
    </div>
  );
}

/**
 * Find a step: its name typed, the steps it matches listed under the field, each with where it stands (on the job, can
 * come next, filed away, not on the job) and what can be done with it: Show it rings it on the board; a step not on the
 * job is added from the list, or put back where it was filed away. A row's name, or Enter, shows the step; Esc closes
 * the list. The list is a set of rows of buttons, never options holding buttons, so each is reached by Tab or the arrows.
 */
function FindAStep({ steps, onFind, heading = 'Steps' }: { steps: FindStep[]; onFind: (key: string) => void; heading?: string }) {
  const id = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => findMatches(steps, text), [steps, text]);
  const shown = open && text.trim().length > 0;
  const choose = (s: FindStep) => {
    setText(stepName(s.title));
    setOpen(false);
    onFind(s.key);
  };
  const act = (s: FindStep) => {
    setOpen(false);
    setText('');
    s.act?.run();
  };
  /** The rows' names, for the arrows. */
  const names = () => [...(box.current?.querySelectorAll<HTMLButtonElement>('[data-find-name]') ?? [])];
  const keys = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      field.current?.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const all = names();
    if (!all.length) return;
    e.preventDefault();
    setOpen(true);
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const to = e.key === 'ArrowDown' ? (at < 0 ? 0 : Math.min(at + 1, all.length - 1)) : at <= 0 ? -1 : at - 1;
    if (to < 0) field.current?.focus();
    else all[to]!.focus();
  };
  return (
    <div
      ref={box}
      className="relative"
      onKeyDown={keys}
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <label className="flex items-center gap-2 text-[0.875rem] text-ink-3">
        Find a step
        <input
          ref={field}
          className="field h-10 w-56"
          aria-controls={`${id}-found`}
          autoComplete="off"
          value={text}
          placeholder="Its name"
          onFocus={() => setOpen(true)}
          // Pressed again once a step was chosen (the field kept its focus), the list opens again.
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hits.length) {
              e.preventDefault();
              const exact = hits.find((s) => stepName(s.title).toLowerCase() === text.trim().toLowerCase());
              choose(exact ?? hits[0]!);
            }
          }}
        />
      </label>
      {shown ? (
        <div
          id={`${id}-found`}
          role="region"
          aria-label={`${heading} found`}
          className="absolute right-0 top-full z-30 mt-1 w-[min(30rem,calc(100vw-2rem))] rounded-[2px] border border-rule-strong bg-sheet shadow-[0_12px_32px_rgba(20,22,19,.10)]"
        >
          <p className="label border-b border-rule px-4 py-2.5" aria-live="polite">
            {heading} · {hits.length ? `${hits.length} found` : 'none found'}
          </p>
          {hits.length ? (
            <ul className="max-h-[24rem] divide-y divide-rule overflow-y-auto">
              {hits.map((s) => (
                <li key={s.key} className="flex items-center gap-3 px-4 py-2 hover:bg-inset/60">
                  <button
                    type="button"
                    data-find-name=""
                    className="min-h-11 min-w-0 flex-1 text-left"
                    // mousedown, not click: it lands before the field's blur closes the list
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(s)}
                  >
                    <span className="block text-[0.9375rem] leading-snug text-ink">{stepName(s.title)}</span>
                    {s.where ? <span className="block text-[0.8125rem] text-ink-3">{s.where}</span> : null}
                  </button>
                  {s.act ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => act(s)}
                      aria-label={`${s.act.word}: ${stepName(s.title)}`}
                      className="inline-flex h-[30px] shrink-0 items-center rounded-[2px] border border-ink bg-sheet px-2.5 text-[0.8125rem] hover:bg-ink hover:text-paper"
                    >
                      {s.act.word}
                    </button>
                  ) : (
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(s)} aria-label={`Show ${stepName(s.title)}`} className="link hit shrink-0 text-[0.875rem] text-ink-2 hover:text-ink">
                      Show it
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-3 text-[0.875rem] text-ink-3">No step is called that.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
